import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase/client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { trackSupabaseError } from '@/lib/analytics/errorHandler';
import { useCallback, useEffect, useRef } from 'react';

export interface UserNotification {
  id: string;
  user_id: string;
  type: 'tip' | 'milestone' | 'feature' | 'system';
  title: string;
  body: string;
  cta_url: string | null;
  cta_label: string | null;
  read_at: string | null;
  created_at: string;
}

const WELCOME_NOTIFICATION: Omit<UserNotification, 'id' | 'user_id' | 'created_at' | 'read_at'> = {
  type: 'milestone',
  title: 'Welcome to SettleWise!',
  body: 'Your personalized divorce strategy is being generated. Explore your dashboard while you wait.',
  cta_url: '/dashboard',
  cta_label: 'Go to Dashboard',
};

export function useNotifications() {
  const { user, profile } = useAuth();
  const queryClient = useQueryClient();
  const seededRef = useRef(false);

  const { data: notifications = [], isLoading } = useQuery({
    queryKey: ['notifications', user?.id],
    queryFn: async (): Promise<UserNotification[]> => {
      if (!user) return [];

      const { data, error } = await (supabase as any)
        .from('user_notifications')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(20);

      if (error) {
        trackSupabaseError('select', 'user_notifications', error);
        return []; // Don't throw — notifications are non-critical
      }

      return (data ?? []) as UserNotification[];
    },
    enabled: !!user,
    staleTime: 1000 * 30, // 30 seconds
  });

  // Real-time: instantly show new notifications (e.g., commission earned)
  useEffect(() => {
    if (!user?.id) return;

    const channel = supabase
      .channel(`notifications:${user.id}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'user_notifications',
        filter: `user_id=eq.${user.id}`,
      }, () => {
        queryClient.invalidateQueries({ queryKey: ['notifications', user.id] });
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [user?.id, queryClient]);

  // Seed welcome notification for new users who completed onboarding
  useEffect(() => {
    if (
      seededRef.current ||
      !user ||
      !profile?.onboarding_complete ||
      profile?.is_advisor ||
      isLoading
    ) {
      return;
    }

    // Only seed if user has zero notifications (first time)
    if (notifications.length === 0) {
      seededRef.current = true;
      try {
        (supabase as any)
          .from('user_notifications')
          .insert({
            user_id: user.id,
            ...WELCOME_NOTIFICATION,
          })
          .then(({ error }: { error: unknown }) => {
            if (!error) {
              queryClient.invalidateQueries({ queryKey: ['notifications', user.id] });
            }
          })
          .catch(() => {
            // Silently ignore — notification seeding is non-critical
          });
      } catch {
        // Silently ignore — notification seeding is non-critical
      }
    }
  }, [user, profile, notifications.length, isLoading, queryClient]);

  const unreadCount = notifications.filter((n) => !n.read_at).length;

  const markAsReadMutation = useMutation({
    mutationFn: async (notificationId: string) => {
      const { error } = await (supabase as any)
        .from('user_notifications')
        .update({ read_at: new Date().toISOString() })
        .eq('id', notificationId)
        .eq('user_id', user?.id);

      if (error) {
        trackSupabaseError('update', 'user_notifications', error);
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications', user?.id] });
    },
  });

  const markAllReadMutation = useMutation({
    mutationFn: async () => {
      if (!user) return;

      const { error } = await (supabase as any)
        .from('user_notifications')
        .update({ read_at: new Date().toISOString() })
        .eq('user_id', user.id)
        .is('read_at', null);

      if (error) {
        trackSupabaseError('update', 'user_notifications', error);
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications', user?.id] });
    },
  });

  const markAsRead = useCallback(
    (notificationId: string) => {
      markAsReadMutation.mutate(notificationId);
    },
    [markAsReadMutation],
  );

  const markAllRead = useCallback(() => {
    markAllReadMutation.mutate();
  }, [markAllReadMutation]);

  return {
    notifications,
    unreadCount,
    isLoading,
    markAsRead,
    markAllRead,
  };
}
